/**
 * VueLayers
 * Web map Vue components with the power of OpenLayers
 *
 * @package vuelayers
 * @author Vladimir Vershinin <ghettovoice@gmail.com>
 * @version 0.12.7
 * @license MIT
 * @copyright (c) 2017-2023, Vladimir Vershinin <ghettovoice@gmail.com>
 */
import { merge, fromEventPattern, Observable } from 'rxjs';
import { map, distinctUntilChanged, tap, debounceTime } from 'rxjs/operators';
import { isFunction, isEqual, identity } from './utils';

/**
 * Creates an Observable using OpenLayers event pattern that emits events coming from the given event target.
 *
 * @example **Subscribe on view center change events**
 * const map = ol.Map({ ... })
 * const changes = Observable.fromOlEvent(map.getView(), 'change:center')
 *
 * changes.subscribe(({ coordinate }) => console.log(coordinate))
 *
 * @param {module:ol/Observable~Observable} target OpenLayers event target.
 * @param {string|Object[]} eventName The event name of interest, being emitted by the `target`
 *                          or an array of events/selectors like `[{ event: 'event1', selector?: x => x }, ...]`.
 * @param {function(...*): *} [selector] An optional function to post-process results. It takes the arguments
 *    from the event handler and should return a single value.
 * @param {Observable[]} [pipeEach]
 * @return {Observable<T>}
 * @memberOf {Observable}
 */

function fromOlEvent(target, eventName, selector) {
  let pipeEach = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : [];

  if (Array.isArray(eventName)) {
    return merge(...eventName.map(elem => {
      let eventName, selector;

      if (typeof elem === 'object') {
        eventName = elem.event;
        selector = elem.selector;
      } else {
        eventName = elem;
      }

      return fromOlEvent(target, eventName, selector).pipe(...pipeEach);
    }));
  }

  selector || (selector = identity);
  return fromEventPattern(handler => target.on(eventName, handler), handler => target.un(eventName, handler)).pipe(map(selector));
}
/**
 * Creates Observable from OpenLayers change:* event
 * @param {module:ol/Observable~Observable} target
 * @param {string|string[]} [prop]
 * @param {boolean|function(a, b):boolean|undefined} [distinct] Distinct values by isEqual fn or by custom comparator
 * @param {function|undefined} [selector] Custom selector
 * @param {Observable[]} [pipeEach]
 * @return {Observable<{prop: string, value: *}>}
 */

function fromOlChangeEvent(target, prop, distinct, selector) {
  let pipeEach = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : [];

  if (Array.isArray(prop)) {
    return merge(...prop.map(p => fromOlChangeEvent(target, p, distinct, selector).pipe(...pipeEach)));
  }

  selector || (selector = identity);
  const event = `change:${prop}`;
  const observable = fromOlEvent(target, event, () => target.get(prop));
  const operations = [];

  if (distinct) {
    isFunction(distinct) || (distinct = isEqual);
    operations.push(distinctUntilChanged(distinct));
  }

  operations.push(map(value => selector({
    prop,
    value
  })));
  return observable.pipe(...operations);
}
function fromVueEvent(target, eventName, selector) {
  if (Array.isArray(eventName)) {
    return merge(...eventName.map(elem => {
      let eventName, selector;

      if (typeof elem === 'object') {
        eventName = elem.event;
        selector = elem.selector;
      } else {
        eventName = elem;
      }

      return fromVueEvent(target, eventName, selector);
    }));
  }

  selector || (selector = identity);
  return fromEventPattern(handler => target.$on(eventName, handler), handler => target.$off(eventName, handler)).pipe(map(selector));
}
function fromVueWatcher(target, exprOrFn) {
  let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return new Observable(s => {
    return target.$watch(exprOrFn, (value, previous) => s.next({
      value,
      previous
    }), options);
  });
}

function bufferDebounceTime() {
  let time = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  return source => {
    let bufferedValues = [];
    return source.pipe(tap(value => bufferedValues.push(value)), debounceTime(time), map(() => bufferedValues), tap(() => {
      bufferedValues = [];
    }));
  };
}

export { bufferDebounceTime, fromOlChangeEvent, fromOlEvent, fromVueEvent, fromVueWatcher };
//# sourceMappingURL=rx-ext.js.map
